/**
 * Planos (spec 004, 7; spec 007, decisao 9 e M6): o catalogo com a familia e a mensalidade de cada plano. Sem familia,
 * nao ha liberacao; sem mensalidade, quem compra fica sem valor ate a equipe informar. Com o modulo `saldo` (spec 043,
 * 3.7), o saldo inicial na corretora de cada plano: a base do resultado. Sem ele, o resultado e "sem base pra calcular".
 * Com o modulo `nelogica-v2` (spec 064, 2.4), a taxa de ativacao de cada plano: aprovar o Challenge cobra este valor
 * (o administrador pode mudar ou dispensar no pedido). Sem taxa, a aprovacao vai direto pra liberacao da Funded.
 * Com o modulo `assinatura` (spec 065, 2.1), a taxa de manutencao de cada plano, em dolar: sem valor proprio, o plano usa
 * o padrao da Configuração da Funded. A parcela que ja nasceu fica com o valor dela (o backend garante).
 */
import { Aviso, Botao, CabecalhoDePagina, CampoDinheiro, CampoSelecao, Carregando, Pilha, Selo, Tabela, Vazio, formatarDolares, formatarMoeda, formatarReais } from "@mtc/ui";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { useParams } from "react-router";
import { ETAPA, FAMILIA, useApi, usePapel, type Familia, type FamiliaDoPlano, type Plano, type TaxaDeAtivacao, type TaxasDeManutencao } from "../api";
import { temPlataformaProfit, useAmbiente } from "../contexto";
import { saldoInicialDe, type SaldoInicialDoPlano } from "./ContasNaCorretora";
import { DialogoDeAcao } from "./DialogoDeAcao";
import { usePlanos } from "./Matriculas";
import { usePagina } from "../pagina";

type MensalidadeDoPlano = { plano_slug: string; profit_one: number | null; profit_pro: number | null };
type Moeda = "USD" | "BRL";
const MOEDAS: { valor: Moeda; rotulo: string }[] = [{ valor: "USD", rotulo: "Dólar (US$)" }, { valor: "BRL", rotulo: "Real (R$)" }];

export function PaginaPlanos() {
  const pag = usePagina("pagina");
  const { slug } = useParams();
  const { ler, por, apagar } = useApi();
  const { modulos } = useAmbiente();
  const plataformaProfit = temPlataformaProfit(modulos);
  const { pode } = usePapel();
  const qc = useQueryClient();
  const planos = usePlanos();
  const comEtapas = modulos.includes("etapas");
  const familias = useQueries({ queries: (planos.data?.itens ?? []).map((p) => ({ queryKey: ["familia", p.slug], queryFn: () => ler<FamiliaDoPlano>(`/v1/admin/etapas/planos/${p.slug}`), enabled: comEtapas })) });
  const familiaDe = new Map(familias.map((f) => [f.data?.plano, f.data]));
  const [editando, setEditando] = useState<Plano | null>(null);
  const [familia, setFamilia] = useState<Familia | "">("");
  const [feito, setFeito] = useState<string>();
  // Mensalidade do plano (spec 007, M6; Vitor, 16/09): mora aqui, nao no checkout. Quando cobra e da regra (M1).
  const comMensalidade = modulos.includes("mensalidade-manual");
  const mensalidades = useQuery({ queryKey: ["mensalidades-dos-planos"], queryFn: () => ler<{ itens: MensalidadeDoPlano[] }>("/v1/admin/planos/mensalidades"), enabled: comMensalidade });
  const mensalidadeDe = new Map((mensalidades.data?.itens ?? []).map((m) => [m.plano_slug, m]));
  const [mensalidade, setMensalidade] = useState<Plano | null>(null);
  const [one, setOne] = useState(0);
  const [pro, setPro] = useState(0);
  const semMensalidade = comMensalidade && mensalidades.data ? (planos.data?.itens ?? []).filter((p) => p.ativo && !mensalidadeDe.get(p.slug)?.profit_one && !mensalidadeDe.get(p.slug)?.profit_pro) : [];
  // Saldo inicial na corretora (spec 043, 3.7): so com o modulo `saldo`. Padrao em dolar, a moeda da conta na Global.
  const comSaldo = modulos.includes("saldo");
  const iniciais = useQuery({ queryKey: ["saldo-dos-planos"], queryFn: () => ler<SaldoInicialDoPlano[]>("/v1/admin/saldo/planos"), enabled: comSaldo });
  const inicialDe = new Map((iniciais.data ?? []).map((i) => [i.plano, i]));
  const [saldoInicial, setSaldoInicial] = useState<Plano | null>(null);
  const [removerInicial, setRemoverInicial] = useState<Plano | null>(null);
  const [valorInicial, setValorInicial] = useState(0);
  const [moedaInicial, setMoedaInicial] = useState<Moeda>("USD");
  const semSaldoInicial = comSaldo && iniciais.data ? (planos.data?.itens ?? []).filter((p) => p.ativo && inicialDe.get(p.slug)?.saldo_inicial_centavos == null) : [];
  // Taxa de ativacao (spec 064): do modulo que e dono do pedido de aprovacao do Challenge.
  const comTaxa = modulos.includes("nelogica-v2");
  const taxas = useTaxasDeAtivacao(comTaxa);
  const taxaDe = new Map((taxas.data?.itens ?? []).map((t) => [t.plano_slug, t.valor_centavos]));
  const [taxa, setTaxa] = useState<Plano | null>(null);
  const [removerTaxa, setRemoverTaxa] = useState<Plano | null>(null);
  const [valorDaTaxa, setValorDaTaxa] = useState(0);
  // Taxa de manutencao (spec 065, 2.1): do modulo da cobranca da Funded. Sem linha, o plano usa o padrao.
  const comManutencao = modulos.includes("assinatura");
  const manutencoes = useQuery({ queryKey: ["taxas-de-manutencao"], queryFn: () => ler<TaxasDeManutencao>("/v1/admin/planos/taxas-de-manutencao"), enabled: comManutencao });
  const manutencaoDe = new Map((manutencoes.data?.itens ?? []).map((t) => [t.plano_slug, t.valor_usd_centavos]));
  const padraoDaManutencao = manutencoes.data?.padrao_usd_centavos;
  const [manutencao, setManutencao] = useState<Plano | null>(null);
  const [voltarAoPadrao, setVoltarAoPadrao] = useState<Plano | null>(null);
  const [valorDaManutencao, setValorDaManutencao] = useState(0);
  const oneNaoPaga = (slug: string) => familiaDe.get(slug)?.familia === "BLACK";
  const semFamilia = comEtapas ? (planos.data?.itens ?? []).filter((p) => familiaDe.has(p.slug) && !familiaDe.get(p.slug)?.familia) : [];
  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina titulo="Planos" descricao={comEtapas ? "Os planos do ambiente e a família de cada um. A família define as etapas e a liberação." : "Os planos do ambiente."} />
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      {semMensalidade.length > 0 && <Aviso tipo="alerta">{semMensalidade.length === 1 ? "Um plano ativo está" : `${semMensalidade.length} planos ativos estão`} sem mensalidade: {semMensalidade.map((p) => p.nome).join(", ")}. Quem comprar fica sem valor e a equipe informa na matrícula.</Aviso>}
      {semSaldoInicial.length > 0 && <Aviso tipo="alerta">{semSaldoInicial.length === 1 ? "Um plano ativo está" : `${semSaldoInicial.length} planos ativos estão`} sem saldo inicial na corretora: {semSaldoInicial.map((p) => p.nome).join(", ")}. Sem ele, o resultado da conta aparece como sem base pra calcular.</Aviso>}
      {semFamilia.length > 0 && <Aviso tipo="alerta">{semFamilia.length === 1 ? "Um plano está" : `${semFamilia.length} planos estão`} sem família: {semFamilia.map((p) => p.nome).join(", ")}. Compras desses planos entram, mas não dá pra liberar.</Aviso>}
      {planos.isLoading ? <Carregando compacto texto="Buscando o catálogo" /> : (
        <Tabela
          paginacao={pag}
          linhas={planos.data?.itens ?? []}
          chave={(p) => p.slug}
          destaque={(p) => p.slug === slug}
          vazio={<Vazio titulo="Catálogo vazio">Nenhum plano cadastrado neste ambiente.</Vazio>}
          colunas={[
            { chave: "plano", rotulo: "Plano", celula: (p) => <><strong>{p.nome}</strong><span className="mtc-tabela__apoio mtc-mono" style={{ whiteSpace: "normal" }}>{slugQueQuebra(p.slug)}</span></> },
            ...(comEtapas
              ? [{ chave: "familia", rotulo: "Família", celula: (p: Plano) => {
                  const f = familiaDe.get(p.slug);
                  if (!f) return <span className="mtc-texto-2">...</span>;
                  return f.familia ? <span className="mtc-linha" style={{ gap: "var(--e-2)" }}><Selo tom="acento">{FAMILIA[f.familia]}</Selo><span className="mtc-tabela__apoio">começa em {f.etapa_inicial ? ETAPA[f.etapa_inicial] : ""}</span></span> : <Selo tom="alerta">Sem família</Selo>;
                } }]
              : []),
            ...(comMensalidade
              ? [{ chave: "mensalidade", rotulo: "Mensalidade", celula: (p: Plano) => {
                  const m = mensalidadeDe.get(p.slug);
                  if (!m?.profit_one && !m?.profit_pro) return <span className="mtc-texto-2">Sem valor</span>;
                  return <span className="mtc-mono">{oneNaoPaga(p.slug) ? "" : `One ${m.profit_one ? formatarReais(m.profit_one) : "sem valor"}, `}Pro {m.profit_pro ? formatarReais(m.profit_pro) : "sem valor"}</span>;
                } }]
              : []),
            ...(comSaldo
              // "Saldo inicial" no cabecalho (o dialogo e o aviso dizem "na corretora"): o rotulo longo, sem quebra, tirava
              // da tabela o espaco que o nome do plano precisa ao lado das duas taxas (spec 065).
              ? [{ chave: "saldo-inicial", rotulo: "Saldo inicial", celula: (p: Plano) => {
                  if (iniciais.isLoading) return <span className="mtc-texto-2">...</span>;
                  const v = saldoInicialDe(inicialDe.get(p.slug));
                  // A acao mora na celula, como a taxa de ativacao: com as duas colunas, a de acoes nao cabia (spec 064).
                  return (
                    <span className="mtc-pilha" style={{ gap: "var(--e-1)", justifyItems: "start" }}>
                      {v ? <span className="mtc-mono">{v}</span> : <span className="mtc-texto-2">Sem saldo inicial</span>}
                      {pode("ADMIN") && (
                        <span className="mtc-linha" style={{ gap: "var(--e-1)" }}>
                          <Botao variante="fantasma" tamanho="pequeno" aria-label={`Saldo inicial do ${p.nome}`} onClick={() => { setFeito(undefined); setSaldoInicial(p); }}>{v ? "Mudar" : "Definir"}</Botao>
                          {inicialDe.get(p.slug)?.saldo_inicial_centavos != null && <Botao variante="fantasma" tamanho="pequeno" aria-label={`Remover o saldo inicial do ${p.nome}`} onClick={() => { setFeito(undefined); setRemoverInicial(p); }}>Remover</Botao>}
                        </span>
                      )}
                    </span>
                  );
                } }]
              : []),
            ...(comTaxa
              ? [{ chave: "taxa-de-ativacao", rotulo: "Taxa de ativação", celula: (p: Plano) => {
                  if (taxas.isLoading) return <span className="mtc-texto-2">...</span>;
                  const v = taxaDe.get(p.slug);
                  // A acao mora na propria celula: a coluna de acoes ja leva o saldo inicial e a tabela nao cabia.
                  return (
                    <span className="mtc-pilha" style={{ gap: "var(--e-1)", justifyItems: "start" }}>
                      {v != null ? <span className="mtc-mono">{formatarReais(v)}</span> : <span className="mtc-texto-2">Sem taxa</span>}
                      {pode("ADMIN") && (
                        <span className="mtc-linha" style={{ gap: "var(--e-1)" }}>
                          <Botao variante="fantasma" tamanho="pequeno" aria-label={`Taxa de ativação do ${p.nome}`} onClick={() => { setFeito(undefined); setTaxa(p); }}>{v != null ? "Mudar" : "Definir"}</Botao>
                          {v != null && <Botao variante="fantasma" tamanho="pequeno" aria-label={`Tirar a taxa de ativação do ${p.nome}`} onClick={() => { setFeito(undefined); setRemoverTaxa(p); }}>Tirar</Botao>}
                        </span>
                      )}
                    </span>
                  );
                } }]
              : []),
            ...(comManutencao
              ? [{ chave: "taxa-de-manutencao", rotulo: "Taxa de manutenção", celula: (p: Plano) => {
                  if (manutencoes.isLoading) return <span className="mtc-texto-2">...</span>;
                  if (manutencoes.isError) return <span className="mtc-texto-2">Não carregou</span>;
                  const v = manutencaoDe.get(p.slug);
                  // Como a taxa de ativacao: a acao mora na celula. Vazio e o padrao, com o valor dele.
                  return (
                    <span className="mtc-pilha" style={{ gap: "var(--e-1)", justifyItems: "start" }}>
                      {v != null ? <span className="mtc-mono">{formatarDolares(v)}</span> : <span className="mtc-texto-2">Padrão{padraoDaManutencao != null ? ` (${formatarDolares(padraoDaManutencao)})` : ""}</span>}
                      {pode("ADMIN") && (
                        <span className="mtc-linha" style={{ gap: "var(--e-1)" }}>
                          <Botao variante="fantasma" tamanho="pequeno" aria-label={`Taxa de manutenção do ${p.nome}`} onClick={() => { setFeito(undefined); setManutencao(p); }}>{v != null ? "Mudar" : "Definir"}</Botao>
                          {v != null && <Botao variante="fantasma" tamanho="pequeno" aria-label={`Voltar a taxa de manutenção do ${p.nome} ao padrão`} onClick={() => { setFeito(undefined); setVoltarAoPadrao(p); }}>Usar o padrão</Botao>}
                        </span>
                      )}
                    </span>
                  );
                } }]
              : []),
            { chave: "contratos", rotulo: "Contratos", celula: (p) => <span className="mtc-mono">{p.contratos ?? ""}</span>, alinhar: "direita" as const },
            { chave: "status", rotulo: "Status", celula: (p) => <Selo tom={p.ativo ? "sucesso" : "neutro"}>{p.ativo ? "Ativo" : "Desativado"}</Selo> },
            { chave: "matriculas", rotulo: "Matrículas", celula: (p) => <span className="mtc-mono">{p.matriculas}</span>, alinhar: "direita" as const },
            ...((comEtapas || comMensalidade) && pode("ADMIN") ? [{ chave: "acoes", rotulo: "Ações", rotuloOculto: true, celula: (p: Plano) => (
              <div className="mtc-linha" style={{ gap: "var(--e-2)" }}>
                {comEtapas && <Botao variante="secundario" tamanho="pequeno" onClick={() => { setFeito(undefined); setEditando(p); }}>Família</Botao>}
                {comMensalidade && <Botao variante="secundario" tamanho="pequeno" onClick={() => { setFeito(undefined); setMensalidade(p); }}>Mensalidade</Botao>}
              </div>
            ) }] : []),
          ]}
        />
      )}
      <DialogoDeAcao
        aberto={Boolean(editando)}
        aoFechar={() => setEditando(null)}
        titulo={`Família do ${editando?.nome ?? "plano"}`}
        descricao="Exame: teste, simulador e mesa real. Black: teste e mesa real. Fast: simulador e mesa real."
        botao="Salvar família"
        podeEnviar={Boolean(familia)}
        aoAbrir={() => setFamilia(editando ? familiaDe.get(editando.slug)?.familia ?? "" : "")}
        aoEnviar={() => por(`/v1/admin/etapas/planos/${editando!.slug}`, { familia })}
        aoConcluir={async () => { await qc.invalidateQueries(); setFeito(`Família do ${editando?.nome} salva.`); }}
      >
        <CampoSelecao rotulo="Família" valor={familia} aoMudar={setFamilia} vazio="Escolha a família" required opcoes={(Object.keys(FAMILIA) as Familia[]).map((f) => ({ valor: f, rotulo: FAMILIA[f] }))} />
      </DialogoDeAcao>
      <DialogoDeAcao
        aberto={Boolean(mensalidade)}
        aoFechar={() => setMensalidade(null)}
        titulo={`Mensalidade do ${mensalidade?.nome ?? "plano"}`}
        descricao="Por mês. Vale pras próximas compras; quem comprou este plano e ainda está sem valor recebe agora. Quem já tem valor fica com o seu (corrige na ficha)."
        botao="Salvar mensalidade"
        podeEnviar={one > 0 || pro > 0}
        aoAbrir={() => { const m = mensalidade ? mensalidadeDe.get(mensalidade.slug) : undefined; setOne(m?.profit_one ?? 0); setPro(m?.profit_pro ?? 0); }}
        aoEnviar={async () => {
          const r = await por<{ preenchidas: number }>(`/v1/admin/planos/${mensalidade!.slug}/mensalidade`, { profit_one: one > 0 && !oneNaoPaga(mensalidade!.slug) ? one : null, profit_pro: pro > 0 ? pro : null });
          setFeito(`Mensalidade do ${mensalidade?.nome} salva.${r.preenchidas ? ` ${r.preenchidas === 1 ? "Uma matrícula sem valor recebeu" : `${r.preenchidas} matrículas sem valor receberam`} agora.` : ""}`);
        }}
        aoConcluir={() => { setMensalidade(null); void qc.invalidateQueries({ queryKey: ["mensalidades-dos-planos"] }); }}
      >
        {/* So onde a plataforma existe (nelogica-v1). Na Global nao ha Profit One nem Pro. */}
        {plataformaProfit && mensalidade && !oneNaoPaga(mensalidade.slug) && <CampoDinheiro rotulo="Profit One" centavos={one} aoMudar={setOne} dica="Paga só no Simulador Remunerado. Na Mesa Real o Profit One é grátis." />}
        <CampoDinheiro rotulo={plataformaProfit ? "Profit Pro" : "Valor da mensalidade"} centavos={pro} aoMudar={setPro} dica={!plataformaProfit ? "O valor cobrado por mês neste plano." : mensalidade && oneNaoPaga(mensalidade.slug) ? "Paga na Mesa Real. No Black o Profit One não tem mensalidade." : "Paga no Simulador Remunerado e na Mesa Real."} />
      </DialogoDeAcao>
      <DialogoDeAcao
        aberto={Boolean(saldoInicial)}
        aoFechar={() => setSaldoInicial(null)}
        titulo={`Saldo inicial do ${saldoInicial?.nome ?? "plano"}`}
        descricao="O saldo com que a conta deste plano começa na corretora. O resultado de cada conta é o saldo menos este valor. Vale na hora pra todas as contas do plano."
        botao="Salvar saldo inicial"
        podeEnviar={valorInicial > 0}
        aoAbrir={() => { const i = saldoInicial ? inicialDe.get(saldoInicial.slug) : undefined; setValorInicial(i?.saldo_inicial_centavos ?? 0); setMoedaInicial(i?.moeda === "BRL" ? "BRL" : "USD"); }}
        aoEnviar={async () => {
          await por(`/v1/admin/saldo/planos/${saldoInicial!.slug}`, { saldo_inicial_centavos: valorInicial, moeda: moedaInicial });
          setFeito(`Saldo inicial do ${saldoInicial?.nome} salvo: ${formatarMoeda(valorInicial, moedaInicial)}.`);
        }}
        aoConcluir={() => { setSaldoInicial(null); void qc.invalidateQueries({ queryKey: ["saldo-dos-planos"] }); void qc.invalidateQueries({ queryKey: ["corretora"] }); }}
      >
        <CampoSelecao rotulo="Moeda" valor={moedaInicial} aoMudar={setMoedaInicial} required opcoes={MOEDAS} dica="A mesma moeda do saldo da conta. Moeda diferente fica sem base pra calcular." />
        <CampoDinheiro rotulo="Saldo inicial na corretora" centavos={valorInicial} aoMudar={setValorInicial} moeda={moedaInicial} dica="Ex.: 25.000,00 no plano de 25K." />
      </DialogoDeAcao>
      <DialogoDeAcao
        aberto={Boolean(removerInicial)}
        aoFechar={() => setRemoverInicial(null)}
        titulo={`Remover o saldo inicial do ${removerInicial?.nome ?? "plano"}`}
        descricao="Sem saldo inicial, o resultado das contas deste plano passa a aparecer como sem base pra calcular, pro cliente e aqui."
        botao="Remover saldo inicial"
        variante="perigo"
        aoEnviar={async () => {
          await apagar(`/v1/admin/saldo/planos/${removerInicial!.slug}`);
          setFeito(`Saldo inicial do ${removerInicial?.nome} removido.`);
        }}
        aoConcluir={() => { setRemoverInicial(null); void qc.invalidateQueries({ queryKey: ["saldo-dos-planos"] }); void qc.invalidateQueries({ queryKey: ["corretora"] }); }}
      />
      <DialogoDeAcao
        aberto={Boolean(taxa)}
        aoFechar={() => setTaxa(null)}
        titulo={`Taxa de ativação do ${taxa?.nome ?? "plano"}`}
        descricao="Cobrada quando o Challenge é aprovado: o cliente paga pela área e a conta Funded só é liberada depois. Vale pras próximas aprovações; quem já foi aprovado fica com o valor da aprovação. No pedido você ainda pode mudar o valor ou dispensar."
        botao="Salvar taxa de ativação"
        podeEnviar={valorDaTaxa > 0}
        aoAbrir={() => setValorDaTaxa(taxa ? taxaDe.get(taxa.slug) ?? 0 : 0)}
        aoEnviar={async () => {
          await por(`/v1/admin/planos/${taxa!.slug}/taxa-de-ativacao`, { valor_centavos: valorDaTaxa });
          setFeito(`Taxa de ativação do ${taxa?.nome} salva: ${formatarReais(valorDaTaxa)}.`);
        }}
        aoConcluir={() => { setTaxa(null); void qc.invalidateQueries({ queryKey: ["taxas-de-ativacao"] }); }}
      >
        <CampoDinheiro rotulo="Taxa de ativação" centavos={valorDaTaxa} aoMudar={setValorDaTaxa} dica="Em reais. Para o plano não cobrar, use Tirar na lista." />
      </DialogoDeAcao>
      <DialogoDeAcao
        aberto={Boolean(removerTaxa)}
        aoFechar={() => setRemoverTaxa(null)}
        titulo={`Tirar a taxa de ativação do ${removerTaxa?.nome ?? "plano"}`}
        descricao={`Hoje ${removerTaxa && taxaDe.has(removerTaxa.slug) ? formatarReais(taxaDe.get(removerTaxa.slug)!) : "sem valor"}. Sem taxa, aprovar o Challenge deste plano vai direto pra liberação da conta Funded. Quem já foi aprovado e está pagando segue com o valor da aprovação.`}
        botao="Deixar sem taxa"
        variante="perigo"
        rotuloFechar="Voltar"
        aoEnviar={async () => {
          await por(`/v1/admin/planos/${removerTaxa!.slug}/taxa-de-ativacao`, { valor_centavos: null });
          setFeito(`O ${removerTaxa?.nome} ficou sem taxa de ativação.`);
        }}
        aoConcluir={() => { setRemoverTaxa(null); void qc.invalidateQueries({ queryKey: ["taxas-de-ativacao"] }); }}
      />
      <DialogoDeAcao
        aberto={Boolean(manutencao)}
        aoFechar={() => setManutencao(null)}
        titulo={`Taxa de manutenção do ${manutencao?.nome ?? "plano"}`}
        descricao="Cobrada a cada ciclo enquanto a conta Funded estiver ativa, em reais pela cotação do dólar do dia em que a parcela nasce. Vale pras próximas parcelas deste plano; a que já nasceu fica com o valor dela."
        botao="Salvar taxa de manutenção"
        podeEnviar={valorDaManutencao > 0}
        aoAbrir={() => setValorDaManutencao(manutencao ? manutencaoDe.get(manutencao.slug) ?? 0 : 0)}
        aoEnviar={async () => {
          await por(`/v1/admin/planos/${manutencao!.slug}/taxa-de-manutencao`, { valor_usd_centavos: valorDaManutencao });
          setFeito(`Taxa de manutenção do ${manutencao?.nome} salva: ${formatarDolares(valorDaManutencao)}.`);
        }}
        aoConcluir={() => { setManutencao(null); void qc.invalidateQueries({ queryKey: ["taxas-de-manutencao"] }); }}
      >
        <CampoDinheiro rotulo="Taxa de manutenção" moeda="USD" centavos={valorDaManutencao} aoMudar={setValorDaManutencao} dica={`Em dólar.${padraoDaManutencao != null ? ` Sem valor próprio, o plano usa o padrão, hoje ${formatarDolares(padraoDaManutencao)}, que muda em Configuração da Funded.` : ""}`} />
      </DialogoDeAcao>
      <DialogoDeAcao
        aberto={Boolean(voltarAoPadrao)}
        aoFechar={() => setVoltarAoPadrao(null)}
        titulo={`Voltar a taxa de manutenção do ${voltarAoPadrao?.nome ?? "plano"} ao padrão`}
        descricao={`Hoje ${voltarAoPadrao && manutencaoDe.has(voltarAoPadrao.slug) ? formatarDolares(manutencaoDe.get(voltarAoPadrao.slug)!) : "sem valor próprio"}. As próximas parcelas deste plano passam a usar o padrão${padraoDaManutencao != null ? `, hoje ${formatarDolares(padraoDaManutencao)}` : ""}. A parcela que já nasceu fica com o valor dela.`}
        botao="Usar o padrão"
        rotuloFechar="Voltar"
        aoEnviar={async () => {
          await por(`/v1/admin/planos/${voltarAoPadrao!.slug}/taxa-de-manutencao`, { valor_usd_centavos: null });
          setFeito(`O ${voltarAoPadrao?.nome} voltou a usar a taxa de manutenção padrão.`);
        }}
        aoConcluir={() => { setVoltarAoPadrao(null); void qc.invalidateQueries({ queryKey: ["taxas-de-manutencao"] }); }}
      />
    </Pilha>
  );
}

/**
 * O slug pode quebrar depois de cada hifen: com as colunas da Global (saldo inicial e as duas taxas, spec 065) o slug
 * inteiro numa linha so empurrava Matriculas pra fora da tabela em 1280 px. O texto continua o mesmo pra quem le e copia.
 * O mono da tabela nao quebra (`white-space: nowrap`, pro valor e o CPF); o slug volta ao normal, ou o WebKit ignora o <wbr>.
 */
const slugQueQuebra = (slug: string) => slug.split("-").map((s, i, todos) => <Fragment key={i}>{s}{i < todos.length - 1 && <>-<wbr /></>}</Fragment>);

/** As taxas de ativacao do catalogo (spec 064, 2.4). Tambem preenchem o valor ao aprovar o Challenge. */
export function useTaxasDeAtivacao(ligado = true) {
  const { ler } = useApi();
  return useQuery({ queryKey: ["taxas-de-ativacao"], queryFn: () => ler<{ itens: TaxaDeAtivacao[] }>("/v1/admin/planos/taxas-de-ativacao"), enabled: ligado });
}
